import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createDetailField, type OrbitResult } from "@/field/detailField";
import { prefersReducedMotion, type Anchor } from "@/field/pointField";
import { usePointField } from "@/field/usePointField";
import { StarIcon } from "@/assets/icons/StarIcon";
import { PlayPauseIcon } from "@/assets/icons/PlayPauseIcon";
import { drillHref, resultsHref, type NavState } from "@/navigation/trail";
import type { Asset, ScoredAsset } from "@/data/types";
import { useFlyingImage } from "@/transitions/FlyingImage";
import { useSaved } from "@/data/useSaved";

interface Props {
  asset: Asset;
  affine: ScoredAsset[];
  state: NavState;
  exiting?: boolean;
  onExit?: (to: string) => void;
}

interface MetaTag {
  label: string;
  kind: "category" | "material" | "acquisition" | "trait";
  query: string;
  weight: number; // drives orbit radius (0–100)
}

const NUCLEUS_DIAMETER = 168;
const RELAX_ITERS = 6;
const RELAX_SPRING = 0.55;
const RELAX_PAD = 6;

type Size = { w: number; h: number };

function repelPair(
  cx: number[],
  cy: number[],
  si: Size,
  sj: Size,
  i: number,
  j: number,
): void {
  const dx = cx[j] - cx[i];
  const dy = cy[j] - cy[i];
  const overlapX = (si.w + sj.w) / 2 + RELAX_PAD - Math.abs(dx);
  const overlapY = (si.h + sj.h) / 2 + RELAX_PAD - Math.abs(dy);
  if (overlapX <= 0 || overlapY <= 0) return;
  if (overlapX < overlapY) {
    const push = overlapX * RELAX_SPRING * (dx < 0 ? -1 : 1);
    cx[i] -= push / 2;
    cx[j] += push / 2;
  } else {
    const push = overlapY * RELAX_SPRING * (dy < 0 ? -1 : 1);
    cy[i] -= push / 2;
    cy[j] += push / 2;
  }
}

function applyRepulsion(
  cx: number[],
  cy: number[],
  sizes: Size[],
  n: number,
): void {
  for (let i = 0; i < n; i++) {
    const si = sizes[i];
    if (!si) continue;
    for (let j = i + 1; j < n; j++) {
      const sj = sizes[j];
      if (!sj) continue;
      repelPair(cx, cy, si, sj, i, j);
    }
  }
}

function applyNuclearClearance(
  cx: number[],
  cy: number[],
  sizes: Size[],
  n: number,
  fcx: number,
  fcy: number,
  nucR: number,
): void {
  for (let i = 0; i < n; i++) {
    const si = sizes[i];
    if (!si) continue;
    const dx = cx[i] - fcx;
    const dy = cy[i] - fcy;
    const dist = Math.hypot(dx, dy);
    const minDist = nucR + si.h / 2 + RELAX_PAD;
    if (dist < minDist && dist > 0.5) {
      const scale = minDist / dist;
      cx[i] = fcx + dx * scale;
      cy[i] = fcy + dy * scale;
    }
  }
}

function applyCaptionFloor(
  cy: number[],
  sizes: Size[],
  n: number,
  capTop: number,
): void {
  for (let i = 0; i < n; i++) {
    const si = sizes[i];
    if (!si) continue;
    const floor = capTop - si.h / 2 - RELAX_PAD;
    if (cy[i] > floor) cy[i] = floor;
  }
}

function applyAnchorPosition(
  el: HTMLElement,
  A: Anchor,
  i: number,
  cache: (Size | undefined)[],
): void {
  el.style.transform = `translate(-50%,-50%) translate(${A[0].toFixed(1)}px,${A[1].toFixed(1)}px)`;
  if (A[3] !== undefined) el.style.opacity = String(A[3]);
  if (!cache[i] && el.offsetWidth > 0)
    cache[i] = { w: el.offsetWidth, h: el.offsetHeight };
}

type NucGeom = { fcx: number; fcy: number; nucR: number };

function relaxAll(
  cx: number[],
  cy: number[],
  sizes: Size[],
  n: number,
  nuc: NucGeom,
  capTop: number,
): void {
  for (let iter = 0; iter < RELAX_ITERS; iter++) {
    applyRepulsion(cx, cy, sizes, n);
    applyNuclearClearance(cx, cy, sizes, n, nuc.fcx, nuc.fcy, nuc.nucR);
    applyCaptionFloor(cy, sizes, n, capTop);
  }
}

const MAX_ORBIT_TAGS = 12;

function kindIcon(kind: MetaTag["kind"]): string {
  if (kind === "category") return "◈";
  if (kind === "material") return "◎";
  if (kind === "acquisition") return "◉";
  return "◇";
}

export default function ExploreTab({
  asset,
  affine: _affine,
  state,
  exiting,
  onExit,
}: Props) {
  const navigate = useNavigate();
  const go = (to: string) => (onExit ? onExit(to) : navigate(to));
  const { toggle, isSaved } = useSaved();

  const fieldRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frontCanvasRef = useRef<HTMLCanvasElement>(null);
  const discRef = useRef<HTMLDivElement>(null);
  const satRefs = useRef<Array<HTMLDivElement | null>>([]);
  const acqRef = useRef<HTMLSpanElement>(null);
  const driftRef = useRef<HTMLElement>(null);

  const [entered, setEntered] = useState({
    core: false,
    cap: false,
    hud: false,
    readout: false,
  });
  const [satsIn, setSatsIn] = useState<boolean[]>(() =>
    new Array(MAX_ORBIT_TAGS).fill(false),
  );
  const [paused, setPaused] = useState(false);
  const [hoveredTag, setHoveredTag] = useState(-1);

  const { land, setFieldRect, setTargetCenter, imageSrc } = useFlyingImage();

  useEffect(() => {
    if (fieldRef.current)
      setFieldRect(fieldRef.current.getBoundingClientRect());
    if (discRef.current) {
      const r = discRef.current.getBoundingClientRect();
      setTargetCenter(r.left + r.width / 2, r.top + r.height / 2);
    }
    const t = window.setTimeout(land, 300);
    return () => window.clearTimeout(t);
  }, []);

  const reduced = useMemo(() => prefersReducedMotion(), []);

  const metaTags = useMemo<MetaTag[]>(() => {
    // Scored pool — add new tag sources here when the real BE shape is known.
    // Sort descending by priority, then cap at MAX_ORBIT_TAGS.
    const pool: (MetaTag & { priority: number })[] = [
      {
        label: asset.categoryLabel,
        kind: "category",
        query: asset.category,
        weight: 92,
        priority: 100,
      },
      {
        label: asset.material,
        kind: "material",
        query: asset.material,
        weight: 84,
        priority: 95,
      },
      ...asset.traits.map((t, i) => ({
        label: t,
        kind: "trait" as const,
        query: t,
        weight: Math.max(52, 68 - i * 3),
        priority: Math.max(50, 80 - i * 3),
      })),
      {
        label: asset.acquisition,
        kind: "acquisition",
        query: asset.acquisition,
        weight: 72,
        priority: 60,
      },
    ];
    return pool
      .toSorted((a, b) => b.priority - a.priority)
      .slice(0, MAX_ORBIT_TAGS)
      .map(({ priority: _p, ...tag }) => tag);
  }, [asset]);

  const results = useMemo<OrbitResult[]>(
    () =>
      metaTags.map((tag) => ({ t: tag.label, r: tag.query, m: tag.weight })),
    [metaTags],
  );

  const onReadout = useCallback(
    ({ lit, drift }: { lit: number; drift: number }) => {
      if (acqRef.current)
        acqRef.current.textContent = lit.toLocaleString("it-IT");
      if (driftRef.current)
        driftRef.current.textContent = `${drift.toFixed(2)} mm/s`;
    },
    [],
  );

  const getNucleusRadius = useCallback(
    () =>
      discRef.current ? discRef.current.offsetWidth / 2 : NUCLEUS_DIAMETER / 2,
    [],
  );

  const field = useMemo(
    () => createDetailField({ results, getNucleusRadius, onReadout }),
    [results, getNucleusRadius, onReadout],
  );

  const captionRef = useRef<HTMLDivElement>(null);
  const satSizeCache = useRef<Array<{ w: number; h: number }>>([]);

  // Invalidate size cache when the tag set changes.
  const prevResultsRef = useRef(results);
  if (prevResultsRef.current !== results) {
    prevResultsRef.current = results;
    satSizeCache.current = [];
  }

  /* Position the satellite labels */
  const onAnchors = useCallback((anchors: Anchor[]) => {
    const n = anchors.length;
    if (!n) return;

    const els = satRefs.current;
    const cache = satSizeCache.current;
    for (let i = 0; i < n; i++) {
      const el = els[i];
      const A = anchors[i];
      if (el && A) applyAnchorPosition(el, A, i, cache);
    }

    if (cache.length < n) return;
    const cx = anchors.map((A) => A[0]);
    const cy = anchors.map((A) => A[1]);

    const field = fieldRef.current;
    if (!field) return;
    const fr = field.getBoundingClientRect();
    const nucR = (discRef.current?.offsetWidth ?? NUCLEUS_DIAMETER) / 2;
    const capTop = captionRef.current
      ? captionRef.current.getBoundingClientRect().top - fr.top
      : fr.height - 80;

    relaxAll(
      cx,
      cy,
      cache,
      n,
      { fcx: fr.width / 2, fcy: fr.height / 2, nucR },
      capTop,
    );

    for (let i = 0; i < n; i++) {
      const el = els[i];
      if (el && anchors[i])
        el.style.transform = `translate(-50%,-50%) translate(${cx[i].toFixed(1)}px,${cy[i].toFixed(1)}px)`;
    }
  }, []);

  const setSatAt = useCallback((i: number) => {
    setSatsIn((s) => s.map((v, j) => (j === i ? true : v)));
  }, []);

  const handleRef = usePointField({
    containerRef: fieldRef,
    canvasRef,
    frontCanvasRef,
    render: field.render,
    onAnchors,
    satMinR: 80,
    resultCount: results.length,
    freezeOnHover: false,
  });

  useEffect(() => {
    if (reduced) {
      setEntered({ core: true, cap: true, hud: true, readout: true });
      setSatsIn(metaTags.map(() => true));
      return;
    }
    const timers = [
      window.setTimeout(() => setEntered((e) => ({ ...e, core: true })), 300),
      window.setTimeout(() => setEntered((e) => ({ ...e, cap: true })), 2600),
      window.setTimeout(
        () => setEntered((e) => ({ ...e, readout: true })),
        2800,
      ),
      window.setTimeout(() => setEntered((e) => ({ ...e, hud: true })), 3200),
      ...metaTags.map((_, i) =>
        window.setTimeout(() => setSatAt(i), 3400 + i * 170),
      ),
    ];
    return () => timers.forEach(window.clearTimeout);
  }, [reduced, metaTags]);

  useEffect(() => {
    if (!exiting) return;
    field.collapse();
    setEntered((e) => ({ ...e, core: false }));
  }, [exiting]);

  const togglePause = () => {
    const next = !paused;
    setPaused(next);
    handleRef.current?.setPaused(next);
  };

  const tagSearchHref = (query: string) => resultsHref({ ...state, query });

  return (
    <div className="stage">
      <div className={`field${exiting ? " exiting" : ""}`} ref={fieldRef}>
        <canvas className="pf" ref={canvasRef} />

        <div className={`core${entered.core && !exiting ? " in" : ""}`}>
          <div className="disc" ref={discRef}>
            <img src={imageSrc} alt="" />
            <span className="ring" />
          </div>
        </div>

        {/* Points nearer than the nucleus centre paint here, so orbits pass IN
            FRONT of the piece. The nucleus is opaque and sits between the two
            canvases; that is the whole depth illusion. */}
        <canvas className="pf front" ref={frontCanvasRef} />

        {/* Metatag satellites — one per tag. Clicking searches by that tag. */}
        {metaTags.map((tag, i) => (
          <div
            key={tag.query + i}
            className={`sat${satsIn[i] && !exiting ? " in" : ""}${hoveredTag === i ? " on" : ""}`}
            ref={(el) => {
              satRefs.current[i] = el;
            }}
            onMouseEnter={() => {
              handleRef.current?.setHover(i);
              setHoveredTag(i);
            }}
            onMouseLeave={() => {
              handleRef.current?.setHover(-1);
              setHoveredTag(-1);
            }}
            onClick={() => go(tagSearchHref(tag.query))}
          >
            <span className="pct tag-kind">{kindIcon(tag.kind)}</span>
            <span className="nm">{tag.label}</span>
          </div>
        ))}

        <div
          ref={captionRef}
          className={`caption${entered.cap && !exiting ? " in" : ""}`}
        >
          <div className="caption-title">
            <h2>{asset.label}</h2>
          </div>
          <div className="rf">
            {asset.ref} · {asset.versions.at(-1)?.id} ·{" "}
            {asset.acquisition.toLowerCase()}
          </div>
          <div className="tools">
            <button
              className="tp ai"
              type="button"
              onClick={() => go(drillHref(asset.ref, state, "similar"))}
            >
              ✦ Trova simili
            </button>
            <button className="tp solid" type="button">
              Ruota in 3D
            </button>
            <button className="tp" type="button">
              Scarica
            </button>
            <button
              className={`tp star-btn${isSaved(asset.ref) ? " saved" : ""}`}
              type="button"
              onClick={() => toggle(asset.ref)}
              aria-label={isSaved(asset.ref) ? "Rimuovi dai salvati" : "Salva"}
              style={{ marginLeft: "auto" }}
            >
              <StarIcon size={16} filled={isSaved(asset.ref)} />
            </button>
          </div>
        </div>

        <div className={`hud${entered.hud && !exiting ? " in" : ""}`}>
          <button
            className="cbtn"
            type="button"
            onClick={togglePause}
            aria-label={paused ? "Riprendi" : "Pausa"}
          >
            <PlayPauseIcon paused={paused} />
          </button>
        </div>

        <div className={`versions-panel${exiting ? " exiting" : ""}`}>
          <h3>◧ Versioni scansione</h3>
          {[...asset.versions].reverse().map((v) => (
            <div
              key={v.id}
              className={`vr${v.status === "corrente" ? " cur" : ""}`}
            >
              <span className="dot" />
              <span className="vt">{v.id}</span>
              <button
                className="sm"
                type="button"
                onClick={() => go(drillHref(asset.ref, state, "version", v.id))}
                title={`Trova scansioni simili alla ${v.id}`}
              >
                ✦ simili
              </button>
              <span className="vd">{v.date}</span>
            </div>
          ))}
        </div>
      </div>

      <aside className={`col${exiting ? " exiting" : ""}`}>
        <h2>◈ Distinta pezzi</h2>
        {(["struttura", "elemento", "chiusura", "pietra"] as const).map(
          (kind) => {
            const parts = asset.bom.filter((p) => p.kind === kind);
            if (!parts.length) return null;
            const kindLabel: Record<string, string> = {
              struttura: "Struttura",
              elemento: "Elementi",
              chiusura: "Chiusure",
              pietra: "Pietre",
            };
            return (
              <div className="grp" key={kind}>
                <h4>{kindLabel[kind]}</h4>
                <table className="bom-table">
                  <tbody>
                    {parts.map((part) => (
                      <tr key={part.code}>
                        <td className="k">{part.label}</td>
                        <td className="qty">
                          {part.qty > 1 ? `×${part.qty}` : ""}
                        </td>
                        <td className="v">{part.note ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          },
        )}
      </aside>
    </div>
  );
}
